// Your routes — and the navigation binding's, which is the part worth knowing.
//
// THIS FILE IS REPLACED when the navigation shell is re-applied. `bottom-tabs`,
// `stack` and `sidebar` produce different routers, so the binding owns it and
// `appfactory spec apply` rewrites it with `overwrite: true`.
//
// It used to claim the opposite — "the factory never replaces it" — which was
// wrong and expensive: an apply silently took a TABS array full of hand-picked
// labels and icons with it, under a message saying four files were written.
// The apply now says loudly when it REPLACES rather than creates.
//
// So: edit it freely, and commit before running `spec apply`. Everything below
// is yours until the shell itself changes.

import { createRouter, createMemoryHistory } from '@ionic/vue-router';

import TabsShell from './pages/TabsShell.vue';

// The screens themselves come from ./product-routes.js, which is generated from
// .appfactory/product-plan.json by `appfactory product apply`.
//
// Two owners, one file each. THIS file is the shell — bottom-tabs, stack and
// sidebar produce different routers, and the binding rewrites it on every
// `spec apply`. The PRODUCT owns which screens exist. Before this split the
// shell hardcoded the worked example's three pages, so re-applying a shell
// silently deleted every screen the app had grown.
import { PRODUCT_ROUTES, DESTINATION_ROUTES, HOME_ROUTE, SETTINGS_ROUTE } from './product-routes.js';

/**
 * A product path, made relative to the tab shell.
 *
 * The plan states routes ABSOLUTELY — `/`, `/tasks/:id` — because that is what
 * a screen's route means to everyone except vue-router. A child route with a
 * leading slash is mounted at the ROOT instead of under its parent, so the
 * shell wraps nothing and its outlet stays empty.
 */
const rel = (p) => String(p).replace(/^\/tabs\//, '').replace(/^\//, '');

/**
 * Where the app opens. main.js pushes this.
 *
 * INSIDE the shell, deliberately. This was `HOME_ROUTE` — the raw plan path —
 * and an app whose primary screen is routed at `/` therefore generated
 * `{ path: '/', redirect: '/' }`. vue-router resolves that redirect, arrives at
 * the same route, resolves it again, and the app dies at boot with
 * `RangeError: Maximum call stack size exceeded`.
 *
 * That throw aborts the rest of main.js — before `router.isReady()`, before
 * `mount('#app')`, before `data-ready` — so `#app` stays empty on every route
 * and every store screenshot comes out blank. Nothing else reports it:
 * `npm run build` succeeds and check_app passed 7/7 against an app that
 * rendered nothing at all.
 *
 * A primary screen at `/` is the common case, and what set_plan encourages.
 */
export const HOME = `/tabs/${rel(HOME_ROUTE)}`;

/**
 * Where the header settings button goes. `about` is `destination: false` in
 * product-routes.js, so it never becomes a tab — it is reached from a
 * trailing settings icon on a tab root's `<AppHeader>` instead, per SHELL.md
 * ("at most one trailing action") and DESIGN.md's "Tab icons and the About
 * settings button" note.
 */
export const SETTINGS_PATH = SETTINGS_ROUTE ? `/tabs/${rel(SETTINGS_ROUTE.path)}` : null;

/** Icons a generated tab falls back to, in order. Reorder or replace freely. */
const TAB_ICONS = [libraryOutline, bagHandleOutline];

// The tabs, in bar order, with the icon each one wears.
//
// `icon` is an IMPORTED ionicon, not a name. `<ion-icon name="home">` fetches
// its SVG from a CDN at runtime, which the app's content-security-policy
// blocks and an offline app could not reach anyway — you get an invisible tab
// bar that works fine on your machine and is blank on a plane.
//
// Every tab is a route. A tab bar is for DESTINATIONS — if your app's main verb
// needs a permanent home, give it a real screen; if it is an action, it belongs
// on the screen it acts on, the way "Add an item" does on Home.
import { libraryOutline, bagHandleOutline, informationCircleOutline } from 'ionicons/icons';

export const TABS = [
  // DESTINATIONS, not PRIMARY_ROUTES.
  //
  // Exactly one screen is `primary` — it names where the app opens — so a tab
  // bar built from it has exactly one tab, and every other top-level screen is
  // unreachable. A parameterised screen marked primary made it worse: the tab
  // carried `href="/recipe/:recipeId/capture"`, a literal `:` in a path that
  // can never resolve. `destination` is the question a tab bar is asking, and
  // it already excludes detail screens and settings.
  ...DESTINATION_ROUTES.map((r, i) => ({
    name: r.id,
    label: r.label,
    // Shell-prefixed, like every other link into the product: a tab pointing at
    // the bare plan path navigates out of the shell and loses the tab bar.
    path: `/tabs/${rel(r.path)}`,
    // A tab without an icon renders as a label floating in empty space, so
    // there is always one. Change them freely — this array is yours.
    icon: TAB_ICONS[i % TAB_ICONS.length],
  })),
  // About is a tab in F (the comp's dock row reads Shelf · Bag · About), as
  // well as the Shelf header's leading action. It stays `destination: false`
  // in the product plan, so it is added here, by the shell, not the plan.
  ...(SETTINGS_PATH ? [{ name: 'about', label: 'About', path: SETTINGS_PATH, icon: informationCircleOutline }] : []),
];

export default createRouter({
  history: createMemoryHistory(),
  routes: [
    { path: '/', redirect: HOME },
    {
      // The tab shell owns its own outlet, so each tab keeps its own
      // navigation stack. Pushing /tabs/items/3 leaves the tab bar in place
      // and the back button working, and switching away and back returns you
      // to the detail screen rather than the top of the list.
      path: '/tabs/',
      component: TabsShell,
      // The empty child is NOT a redirect. Once paths are relative the home
      // screen occupies that slot itself, and sending it onward to a route that
      // resolves back here is the loop described on HOME above.
      children: [
        // `props: true` on every route, so a screen with `:id` in its path gets
        // it as a prop as well as through useRoute(). A detail screen that has
        // to reach for the router to learn which record it is showing is a
        // screen that cannot be tested without one.
        ...PRODUCT_ROUTES.map((r) => ({
          path: rel(r.path),
          name: r.id,
          component: r.component,
          props: true,
        })),
      ],
    },
  ],
});
